import { ERemoveBaseContentOption } from '@constants';
import { IActivity } from '@sdk/contracts';
import { Button, Checkbox, CheckboxChangeEvent, Flex, Modal, Radio, RadioGroupProps } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';

import {
  RadioDescriptionWrapper,
  RadioGroupWrapper,
  RadioTitleWrapper
} from './RemoveBaseContentActivity.styles';

import { Icon } from '@/components/Icon';
import { useMutateActivity } from '@/store';

type Props = {
  activity: IActivity;
  sectionId: string;
  buttonType?: 'text' | 'primary';
  fullWidth?: boolean;
  handleClose?: () => void;
};

const MODAL_WIDTH = 572;

export const RemoveBaseContentActivity: React.FC<Props> = ({
  activity,
  sectionId,
  buttonType = 'primary',
  fullWidth = false,
  handleClose
}) => {
  const { isWorking, removeActivity } = useMutateActivity();
  const { t } = useTranslation(['CourseTranslations', 'DefaultTranslations']);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [deleteOption, setDeleteOption] = useState<ERemoveBaseContentOption>(
    ERemoveBaseContentOption.ONLY_BASE_CONTENT
  );

  const handleDeleteActivity = () =>
    removeActivity(
      {
        activityId: activity._id,
        sectionId,
        removeSyncedActivities: deleteOption
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          handleClose?.();
        }
      }
    );

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsConfirmed(!isConfirmed);
  };

  const handleRadioChange: RadioGroupProps['onChange'] = e => {
    setDeleteOption(e.target.value);
  };

  return (
    <>
      <Button
        danger
        type={buttonType}
        loading={isWorking}
        style={{ width: fullWidth ? '100%' : undefined }}
        onClick={e => {
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        icon={<Icon i="TrashCan" />}>
        {t('CourseTranslations:DELETE_ACTIVITY')}
      </Button>

      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        okText={t('CourseTranslations:DELETE_ACTIVITY')}
        cancelText={t('DefaultTranslations:CANCEL')}
        onOk={handleDeleteActivity}
        centered
        width={MODAL_WIDTH}
        okButtonProps={{ disabled: !isConfirmed }}
        title={t('CourseTranslations:DELETE_ACTIVITY_QUESTION')}
        wrapProps={{ onClick: (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation() }}>
        <Flex vertical gap={24} style={{ paddingTop: 16, paddingBottom: 16 }}>
          <span>
            <Trans
              i18nKey="CourseTranslations:ACTIVITY_IS_BEING_USED_IN_OTHER_COURSES"
              values={{ name: activity.name }}
              components={{ bold: <strong /> }}
            />
          </span>

          <Flex vertical gap={16}>
            <RadioTitleWrapper>
              {t('CourseTranslations:DECIDE_HOW_YOU_WANT_TO_DELETE')}
            </RadioTitleWrapper>

            <Radio.Group value={deleteOption} onChange={handleRadioChange}>
              <RadioGroupWrapper>
                <Flex vertical>
                  <Radio value={ERemoveBaseContentOption.ONLY_BASE_CONTENT}>
                    <span>{t('CourseTranslations:ONLY_IN_BASIC_CONTENT')}</span>
                  </Radio>

                  <ul>
                    <li>{t('CourseTranslations:ONLY_IN_BASIC_CONTENT_ACTIVITY_1')}</li>
                    <li>{t('CourseTranslations:ONLY_IN_BASIC_CONTENT_ACTIVITY_2')}</li>
                  </ul>
                </Flex>

                <Flex vertical>
                  <Radio value={ERemoveBaseContentOption.REMOVE_SYNCED_CONTENT}>
                    <span>{t('CourseTranslations:BASIC_CONTENT_AND_OTHER_COURSES')}</span>
                  </Radio>
                  <RadioDescriptionWrapper>
                    {t('CourseTranslations:BASIC_CONTENT_AND_OTHER_COURSES_ACTIVITY_DESCRIPTION')}
                  </RadioDescriptionWrapper>
                </Flex>
              </RadioGroupWrapper>
            </Radio.Group>
          </Flex>

          <Checkbox checked={isConfirmed} onChange={handleCheckboxChange}>
            {t('DefaultTranslations:I_CONFIRM_I_UNDERSTAND')}
          </Checkbox>
        </Flex>
      </Modal>
    </>
  );
};
